import { useEffect, useState } from 'react';
import axios from 'axios';
import { FaSave, FaTimes } from 'react-icons/fa';
import { formatRupiah } from '../../../helper/helper';

const PaymentForm = ({
    handleSubmit,
    item,
    data,
    submitting = false,
    onCancel,
}) => {
    const header = {
        headers: { Authorization: `Bearer ${data?.token}` },
    };

    const isEdit = Boolean(item?.trxID);
    const isLoaLinked = Boolean(item?.loa_id);
    const loaReadOnlyClass = 'input w-full cursor-not-allowed opacity-60';

    const initializeAmount = (source) => {
        if (!source?.amount) return '';
        const raw =
            typeof source.amount === 'number'
                ? source.amount.toString()
                : source.amount.toString().replace(/[^\d]/g, '');
        return formatRupiah(raw);
    };

    const [amount, setAmount] = useState(() => initializeAmount(item));
    const [listPaymentMethod, setListPaymentMethod] = useState([]);
    const [listJournal, setListJournal] = useState([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');

    useEffect(() => {
        axios
            .get('api/payment-method', header)
            .then((response) => setListPaymentMethod(response.data || []))
            .catch((error) => console.error('Error fetching payment methods:', error));

        axios
            .get('api/journal?page=1&limit=100', header)
            .then((response) => setListJournal(response.data?.items || []))
            .catch((error) => console.error('Error fetching journals:', error));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setAmount(initializeAmount(item));

        if (item?.paymentMethodID) {
            const paymentMethodId =
                typeof item.paymentMethodID === 'object'
                    ? item.paymentMethodID?.paymentMethodID
                    : item.paymentMethodID;
            setSelectedPaymentMethod(paymentMethodId?.toString() || '');
        } else {
            setSelectedPaymentMethod('');
        }
        // eslint-disable-next-line
    }, [item]);

    const handleAmountChange = (e) => {
        const rawValue = e.target.value.replace(/[^\d]/g, '');
        setAmount(formatRupiah(rawValue));
    };

    const statusClass =
        item?.paymentStatus === 'lunas'
            ? 'tag-accent'
            : 'tag';

    return (
        <form onSubmit={handleSubmit} className="card w-full overflow-hidden">
            <div className="flex flex-col gap-2 border-b border-hairline px-4 py-4 dark:border-white/10 md:flex-row md:items-center md:justify-between md:px-6">
                <div>
                    <h2 className="text-lg font-semibold text-ink dark:text-white">
                        {isEdit ? 'Edit Payment' : 'Tambah Payment'}
                    </h2>
                    {isEdit ? (
                        <p className="mt-0.5 text-sm text-muted">
                            Perbarui data transaksi pembayaran
                        </p>
                    ) : null}
                </div>
                {isEdit ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="tag font-mono text-xs">{item.trxID}</span>
                        {item.paymentStatus ? (
                            <span className={statusClass}>{item.paymentStatus}</span>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {isEdit ? (
                <input type="hidden" name="trxID" value={item.trxID} />
            ) : null}

            <div className="card-body space-y-8">
                <section>
                    <h3 className="form-section-title">Produk</h3>
                    {isLoaLinked ? (
                        <p className="mb-4 text-sm text-muted">
                            Jurnal dan judul artikel diambil dari LOA
                            {item?.loa_submitID ? ` (${item.loa_submitID})` : ''}
                            {' — '}
                            ubah melalui halaman edit LOA.
                        </p>
                    ) : null}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div>
                            <label htmlFor="journalName" className="form-label">
                                Jurnal
                            </label>
                            {isLoaLinked ? (
                                <>
                                    <input
                                        type="hidden"
                                        name="journalName"
                                        value={item?.journalName || ''}
                                    />
                                    <select
                                        id="journalName"
                                        defaultValue={item?.journalName || ''}
                                        className={loaReadOnlyClass}
                                        disabled
                                    >
                                        <option value={item?.journalName || ''}>
                                            {item?.journalName || '-'}
                                        </option>
                                    </select>
                                </>
                            ) : (
                                <select
                                    id="journalName"
                                    name="journalName"
                                    defaultValue={item?.journalName || ''}
                                    className="input w-full"
                                    required
                                >
                                    <option value="">Pilih jurnal</option>
                                    {listJournal.map((journal) => (
                                        <option
                                            key={journal.journal_id}
                                            value={journal.journal_name}
                                        >
                                            {journal.journal_name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <div className="sm:col-span-2 lg:col-span-3">
                            <label htmlFor="articleName" className="form-label">
                                Judul artikel
                            </label>
                            {isLoaLinked ? (
                                <>
                                    <input
                                        type="hidden"
                                        name="articleName"
                                        value={item?.articleName || ''}
                                    />
                                    <textarea
                                        id="articleName"
                                        rows={3}
                                        className={`${loaReadOnlyClass} min-h-[88px] resize-none !h-auto py-2.5`}
                                        disabled
                                        readOnly
                                        value={item?.articleName || ''}
                                    />
                                </>
                            ) : (
                                <textarea
                                    id="articleName"
                                    name="articleName"
                                    placeholder="Judul artikel"
                                    rows={3}
                                    className="input min-h-[88px] w-full resize-y !h-auto py-2.5"
                                    required
                                    defaultValue={item?.articleName || ''}
                                />
                            )}
                        </div>
                    </div>
                </section>

                <section className="border-t border-hairline pt-8 dark:border-white/10">
                    <h3 className="form-section-title">Customer</h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div>
                            <label htmlFor="paymentName" className="form-label">
                                Nama transaksi
                            </label>
                            <input
                                id="paymentName"
                                type="text"
                                name="paymentName"
                                placeholder="Nama transaksi"
                                className="input w-full"
                                required
                                defaultValue={item?.paymentName || ''}
                            />
                        </div>
                        <div>
                            <label htmlFor="customerName" className="form-label">
                                Nama customer
                            </label>
                            <input
                                id="customerName"
                                type="text"
                                name="customerName"
                                placeholder="Nama customer"
                                className="input w-full"
                                required
                                defaultValue={item?.customerName || ''}
                            />
                        </div>
                        <div>
                            <label htmlFor="customerPhone" className="form-label">
                                Telepon
                            </label>
                            <input
                                id="customerPhone"
                                type="text"
                                name="customerPhone"
                                placeholder="08xxxxxxxxxx"
                                className="input w-full"
                                required
                                defaultValue={item?.customerPhone || ''}
                            />
                        </div>
                        <div>
                            <label htmlFor="customerEmail" className="form-label">
                                Email
                            </label>
                            <input
                                id="customerEmail"
                                type="email"
                                name="customerEmail"
                                placeholder="email@example.com"
                                className="input w-full"
                                required
                                defaultValue={item?.customerEmail || ''}
                            />
                        </div>
                        <div className="sm:col-span-2 lg:col-span-3">
                            <label htmlFor="customerAddress" className="form-label">
                                Alamat
                            </label>
                            <textarea
                                id="customerAddress"
                                name="customerAddress"
                                placeholder="Alamat lengkap customer"
                                rows={2}
                                className="input min-h-[72px] w-full resize-y !h-auto py-2.5"
                                required
                                defaultValue={item?.customerAddress || ''}
                            />
                        </div>
                    </div>
                </section>

                <section className="border-t border-hairline pt-8 dark:border-white/10">
                    <h3 className="form-section-title">Pembayaran</h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div>
                            <label htmlFor="paymentStatus" className="form-label">
                                Status
                            </label>
                            <select
                                id="paymentStatus"
                                name="paymentStatus"
                                defaultValue={item?.paymentStatus || 'pending'}
                                className="input w-full"
                                required
                            >
                                <option value="pending">Pending</option>
                                <option value="lunas">Lunas</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="amount" className="form-label">
                                Jumlah
                            </label>
                            <div className="relative">
                                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted">
                                    Rp
                                </span>
                                <input
                                    id="amount"
                                    type="text"
                                    name="amount"
                                    placeholder="0"
                                    className="input w-full !pl-10"
                                    required
                                    value={amount}
                                    onChange={handleAmountChange}
                                />
                            </div>
                        </div>
                        <div className="sm:col-span-2 lg:col-span-3">
                            <label htmlFor="paymentMethodID" className="form-label">
                                Metode
                            </label>
                            <select
                                id="paymentMethodID"
                                name="paymentMethodID"
                                value={selectedPaymentMethod}
                                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                className="input w-full"
                                required
                            >
                                <option value="">Pilih metode</option>
                                {listPaymentMethod.map((method) => (
                                    <option
                                        key={method.paymentMethodID}
                                        value={method.paymentMethodID}
                                    >
                                        {method.bank} · {method.rekening_number}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </section>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-hairline bg-surface-soft px-4 py-4 dark:border-white/10 dark:bg-white/[0.03] sm:flex-row sm:justify-end md:px-6">
                {onCancel ? (
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={submitting}
                        className="btn-secondary w-full sm:w-auto"
                    >
                        <FaTimes className="text-xs" />
                        Batal
                    </button>
                ) : null}
                <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full sm:w-auto disabled:opacity-50"
                >
                    <FaSave className="text-xs" />
                    {submitting ? 'Menyimpan...' : 'Simpan'}
                </button>
            </div>
        </form>
    );
};

export default PaymentForm;
